import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import useLiveBoard from "../hooks/useLiveBoard";
import { deltaOf, sparkline } from "../data/mockData";
import { PUBLIC } from "../config";

const fmt = (v, d = 2) => (v == null ? "—" : Number(v).toFixed(d));

// ─────────────────────────────────────────────────────────────────────────────

function SessionChip({ s }) {
  if (s === "HALT") return <span className="chip chip-halt">Halt</span>;
  if (s === "AH") return <span className="chip chip-ah">AH</span>;
  return <span className="chip">RTH</span>;
}

// Longer form for the ticket header
function SessionChipLong({ s }) {
  if (s === "HALT") return <span className="chip chip-halt">Halt</span>;
  if (s === "AH") return <span className="chip chip-ah">After hours</span>;
  return <span className="chip">RTH</span>;
}

function DeltaText({ d, size = 12.5 }) {
  if (d == null) return <span style={{ color: "var(--ink-muted)", fontSize: size }}>—</span>;
  const cls = d === 0 ? "" : d > 0 ? "up" : "down";
  const sign = d > 0 ? "+" : d < 0 ? "−" : "±";
  return <span className={cls} style={{ fontSize: size }}>{sign}{Math.abs(d).toFixed(2)}%</span>;
}

// ─────────────────────────────────────────────────────────────────────────────
// LEFT: live board

function Board({ rows, selected, onSelect }) {
  return (
    <div style={{ borderTop: "1px solid var(--line)" }} data-testid="board">
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "68px 1fr 1fr 1fr 82px",
          gap: 8,
          padding: "10px 20px",
          borderBottom: "1px solid var(--line)",
          fontSize: 10.5,
          letterSpacing: "0.16em",
          textTransform: "uppercase",
          color: "var(--ink-muted)",
        }}
      >
        <div>Ticker</div>
        <div>Pool</div>
        <div>NAV</div>
        <div>Δ</div>
        <div style={{ textAlign: "right" }}>Session</div>
      </div>
      {rows.map((r) => {
        const d = deltaOf(r);
        const isSel = r.ticker === selected;
        return (
          <div
            key={r.ticker}
            className={`row ${isSel ? "selected" : ""}`}
            data-testid={`row-${r.ticker}`}
            onClick={() => onSelect(r.ticker)}
          >
            <div className="t">{r.ticker}</div>
            <div className="num">{fmt(r.pool)}</div>
            <div className="num muted">{fmt(r.nav)}</div>
            <div className="delta"><DeltaText d={d} size={12.5} /></div>
            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              <SessionChip s={r.session} />
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// RIGHT: inline ticket

function Sparklines({ ticker, delta, halted }) {
  const d = delta == null ? 0 : delta;
  const pool = useMemo(() => sparkline(ticker.charCodeAt(0) + 1, d), [ticker, d]);
  const nav  = useMemo(() => sparkline(ticker.charCodeAt(0) + 9, d * 0.55), [ticker, d]);
  const W = 520, H = 140;
  const min = Math.min(...pool, ...nav);
  const max = Math.max(...pool, ...nav);
  const rng = max - min || 1;
  const step = W / (pool.length - 1);
  const build = (pts) =>
    pts
      .map((y, i) => {
        const px = (i * step).toFixed(2);
        const py = (H - ((y - min) / rng) * (H - 20) - 10).toFixed(2);
        return `${i === 0 ? "M" : "L"}${px},${py}`;
      })
      .join(" ");
  return (
    <svg width="100%" height={H} viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" data-testid="ticket-chart">
      <path d={build(nav)}  stroke="var(--line)" strokeWidth="1.25" fill="none" strokeDasharray="3 3" />
      <path d={build(pool)} stroke={halted ? "var(--ink-muted)" : "var(--ink)"} strokeWidth="1.5" fill="none" />
    </svg>
  );
}

function Ticket({ row }) {
  const [side, setSide] = useState("buy");
  const [pay, setPay] = useState("USDG");
  const [size, setSize] = useState("");
  const halted = row.session === "HALT";
  const d = deltaOf(row);

  return (
    <div className="snap home-ticket" key={row.ticker} data-testid="ticket">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 12, flexWrap: "wrap" }}>
        <div style={{ display: "flex", alignItems: "baseline", gap: 14, flexWrap: "wrap" }}>
          <div className="wordmark home-ticket-word" style={{ fontSize: 60, lineHeight: 1, letterSpacing: "-0.06em" }} data-testid="ticket-ticker">{row.ticker}</div>
          <SessionChipLong s={row.session} />
        </div>
        <Link
          to={`/listed/${row.ticker.toLowerCase()}`}
          className="btn-ghost"
          style={{ textDecoration: "none" }}
          data-testid="ticket-open"
        >
          Open ticket
        </Link>
      </div>
      <div style={{ color: "var(--ink-muted)", fontSize: 11, letterSpacing: "0.14em", textTransform: "uppercase", marginTop: 6 }}>
        {row.name}
      </div>

      <div
        className="tabular"
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr 1fr",
          gap: 24,
          marginTop: 22,
          borderTop: "1px solid var(--line)",
          borderBottom: "1px solid var(--line)",
          padding: "16px 0",
        }}
      >
        <div>
          <div style={{ fontSize: 10.5, letterSpacing: "0.16em", textTransform: "uppercase", color: "var(--ink-muted)" }}>Pool</div>
          <div style={{ fontSize: 26, letterSpacing: "-0.02em", marginTop: 4 }}>{fmt(row.pool)}</div>
        </div>
        <div>
          <div style={{ fontSize: 10.5, letterSpacing: "0.16em", textTransform: "uppercase", color: "var(--ink-muted)" }}>NAV</div>
          <div style={{ fontSize: 26, letterSpacing: "-0.02em", marginTop: 4, color: "var(--ink-muted)" }}>{fmt(row.nav)}</div>
        </div>
        <div>
          <div style={{ fontSize: 10.5, letterSpacing: "0.16em", textTransform: "uppercase", color: "var(--ink-muted)" }}>Δ</div>
          <div style={{ marginTop: 4 }}><DeltaText d={d} size={20} /></div>
        </div>
      </div>

      <div style={{ marginTop: 20 }}>
        <div style={{ display: "flex", gap: 18, fontSize: 11, color: "var(--ink-muted)", letterSpacing: "0.14em", textTransform: "uppercase", marginBottom: 8 }}>
          <span>Pool</span>
          <span>NAV</span>
          <span style={{ marginLeft: "auto" }}>Route Uniswap · Quote USDG</span>
        </div>
        <Sparklines ticker={row.ticker} delta={d} halted={halted} />
      </div>

      {/* Buy / Sell */}
      <div style={{ display: "flex", gap: 0, marginTop: 22 }}>
        <button
          onClick={() => setSide("buy")}
          data-testid="side-buy"
          disabled={halted}
          style={{
            flex: 1, padding: "12px 0", fontSize: 12, cursor: halted ? "not-allowed" : "pointer",
            background: !halted && side === "buy" ? "var(--ink)" : "transparent",
            color: !halted && side === "buy" ? "var(--paper)" : "var(--ink)",
            border: "1px solid var(--ink)", borderRight: "none",
            opacity: halted ? 0.35 : 1,
            letterSpacing: "0.06em",
          }}
        >BUY</button>
        <button
          onClick={() => setSide("sell")}
          data-testid="side-sell"
          disabled={halted}
          style={{
            flex: 1, padding: "12px 0", fontSize: 12, cursor: halted ? "not-allowed" : "pointer",
            background: !halted && side === "sell" ? "var(--ink)" : "transparent",
            color: !halted && side === "sell" ? "var(--paper)" : "var(--ink)",
            border: "1px solid var(--ink)",
            opacity: halted ? 0.35 : 1,
            letterSpacing: "0.06em",
          }}
        >SELL</button>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginTop: 14 }}>
        <div>
          <label style={{ fontSize: 10.5, letterSpacing: "0.16em", textTransform: "uppercase", color: "var(--ink-muted)" }}>Pay</label>
          <select
            value={pay}
            onChange={(e) => setPay(e.target.value)}
            data-testid="pay-select"
            disabled={halted}
            style={{
              width: "100%", padding: "10px 8px", marginTop: 6,
              background: "transparent", border: "1px solid var(--line)", color: "var(--ink)", fontSize: 13,
              borderRadius: 0,
            }}
          >
            <option>USDG</option>
          </select>
        </div>
        <div>
          <label style={{ fontSize: 10.5, letterSpacing: "0.16em", textTransform: "uppercase", color: "var(--ink-muted)" }}>Size</label>
          <input
            value={size}
            onChange={(e) => setSize(e.target.value.replace(/[^0-9.]/g, ""))}
            placeholder="0.00"
            data-testid="size-input"
            disabled={halted}
            style={{
              width: "100%", padding: "10px 8px", marginTop: 6,
              background: "transparent", border: "1px solid var(--line)", color: "var(--ink)", fontSize: 14,
              outline: "none", borderRadius: 0,
            }}
          />
        </div>
      </div>

      <Link
        to={halted ? "#" : `/listed/${row.ticker.toLowerCase()}?side=${side}${size ? "&size=" + encodeURIComponent(size) : ""}`}
        className="btn-primary"
        data-testid="submit-trade"
        style={{ width: "100%", padding: "14px", fontSize: 13, marginTop: 16, letterSpacing: "0.06em", textDecoration: "none", pointerEvents: halted ? "none" : "auto", opacity: halted ? 0.35 : 1 }}
      >
        {halted ? "HALT" : `${side === "buy" ? "BUY" : "SELL"} ${row.ticker}`}
      </Link>

      <div className="tabular" style={{ display: "flex", justifyContent: "space-between", fontSize: 11, letterSpacing: "0.08em", color: "var(--ink-muted)", marginTop: 14, textTransform: "uppercase" }}>
        <span>Depth {size ? Math.min(9.9, Number(size)/5000).toFixed(2) : "0.00"}% of pool</span>
        <span>Chain 4663</span>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Home

const FILINGS = [
  ["/listed", "Official list.", "If it is not on the list it is not that name."],
  ["/listed", "Session.",       "RTH. After hours. Halt."],
  ["/new",    "New.",           "Curves from pads that already exist."],
  ["/pairs",  "Pairs.",         "Uniswap v2, v3, v4. A listed name can be the quote."],
];

const QUIET = [
  ["/book",  "Range",     "Set the band here, fill it on Delta."],
  ["/book",  "Fee share", "Trades, pool, holders."],
  ["/book",  "Book",      "Listed names and pairs together."],
  ["/docs",  "Preview",   PUBLIC ? "Public access is open." : "Public access is not open."],
];

export default function Home() {
  const rows = useLiveBoard(1400);
  const [selected, setSelected] = useState("NVDA");
  const row = rows.find((r) => r.ticker === selected) || rows[0];

  return (
    <div>
      {/* SCREEN 1 — board + ticket */}
      <section
        style={{
          maxWidth: 1520,
          margin: "0 auto",
          padding: "20px 28px 32px",
        }}
        data-testid="home-terminal"
      >
        <div
          className="nav-home-grid"
          style={{
            display: "grid",
            gridTemplateColumns: "minmax(0, 55fr) minmax(0, 45fr)",
            gap: 28,
            alignItems: "flex-start",
          }}
        >
          <div style={{ border: "1px solid var(--line)", background: "var(--paper-raised)" }}>
            <Board rows={rows} selected={selected} onSelect={setSelected} />
          </div>

          <div style={{ padding: "4px 8px" }}>
            <Ticket row={row} />
          </div>
        </div>

        {/* Single filing sentence */}
        <div
          className="serif"
          style={{
            fontSize: 26,
            marginTop: 40,
            letterSpacing: "-0.01em",
            color: "var(--ink)",
          }}
          data-testid="home-filing"
        >
          Two prices on every listed name.
        </div>
      </section>

      {/* SCREEN 2 — four filings */}
      <section
        style={{
          maxWidth: 1520,
          margin: "0 auto",
          padding: "72px 28px 24px",
          borderTop: "1px solid var(--line)",
        }}
        data-testid="filings"
      >
        <div className="nav-4col" style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 40 }}>
          {FILINGS.map(([to, h, l]) => (
            <Link
              key={h}
              to={to}
              data-testid={`filing-${h.replace(/[^a-z]/gi, "").toLowerCase()}`}
              style={{ borderTop: "1px solid var(--line)", paddingTop: 16, textDecoration: "none", color: "var(--ink)", transition: "background-color 150ms ease" }}
              onMouseEnter={(e) => (e.currentTarget.style.background = "var(--paper-raised)")}
              onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
            >
              <div className="serif" style={{ fontSize: 26, letterSpacing: "-0.01em", marginBottom: 8 }}>{h}</div>
              <div style={{ fontSize: 14, lineHeight: 1.45, color: "var(--ink)", maxWidth: "26ch" }}>{l}</div>
            </Link>
          ))}
        </div>
      </section>

      {/* SCREEN 3 — quiet panel */}
      <section
        style={{
          maxWidth: 1520,
          margin: "0 auto",
          padding: "72px 28px 0",
        }}
        data-testid="quiet-panel"
      >
        <div className="nav-4col" style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 0, border: "1px solid var(--line)", background: "var(--paper-raised)" }}>
          {QUIET.map(([to, h, l], i) => (
            <Link
              key={h}
              to={to}
              data-testid={`quiet-${h.replace(/\s+/g, "-").toLowerCase()}`}
              style={{
                padding: "26px 24px",
                borderLeft: i === 0 ? "none" : "1px solid var(--line)",
                textDecoration: "none",
                color: "var(--ink)",
                transition: "background-color 150ms ease",
              }}
              onMouseEnter={(e) => (e.currentTarget.style.background = "var(--paper)")}
              onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
            >
              <div className="grotesque" style={{ fontSize: 11, letterSpacing: "0.18em", textTransform: "uppercase", color: "var(--ink-muted)", marginBottom: 12 }}>{h}</div>
              <div style={{ fontSize: 15, lineHeight: 1.4 }}>{l}</div>
            </Link>
          ))}
        </div>
      </section>
    </div>
  );
}
